import { useEffect } from "react";
import { Modal, Form, Button, Tabs, Space, notification } from "antd";
import LocationPicker from "./LocationPicker";

import useDevices from "../hooks/useDevices";
import useEmissionRates from "../hooks/useEmissionRates";
import useMinPpmByElement from "../hooks/useMinPpmByElement";
import useSolarRadiation from "../hooks/useSolarRadiation";
import useEmissionPointWizard from "../hooks/useEmissionPointWizard";

import EmissionPointBasicInfoTab from "./EmissionPointBasicInfoTab";
import EmissionRatesTab from "./EmissionRatesTab";
import MinPpmByElementTab from "./MinPpmByElementTab";
import SolarRadiationTab from "./SolarRadiationTab";

import { useGeoMapModal, DEFAULT_MAP_POSITION } from "../utils/geoFormUtils";

const defaultPosition = DEFAULT_MAP_POSITION;

export default function EmissionPointModal({
  open,
  onCancel,
  onSave,
  mode,
  selectedEmissionPoint,
  saving,
  form,
}) {
  const { devicesWithAnemometer } = useDevices();
  const wizard = useEmissionPointWizard({ mode, form });

  const emissionPointId = selectedEmissionPoint?.id;

  const emissionRatesHook = useEmissionRates(emissionPointId);
  const minPpmHook = useMinPpmByElement(emissionPointId);
  const solarRadiationHook = useSolarRadiation(emissionPointId);

  const {
    markerPosition,
    showMap,
    expanded,
    expandedMapKey,
    mapRefreshKey,
    setSmallMap,
    setExpandedMap,
    updateMarkerPosition,
    openExpandedMap,
    closeExpandedMap,
  } = useGeoMapModal({
    open,
    lat: selectedEmissionPoint?.latitude,
    lng: selectedEmissionPoint?.longitude,
    defaultPosition,
  });

  const defaultSolarRadiationDubai = [
    0, 0, 0, 0, 0, 0,
    100, 300, 550, 750, 900, 1000,
    1050,
    1000, 900, 750, 550, 300, 100,
    0, 0, 0, 0, 0,
  ].map((radiation, hour) => ({ hour, radiation }));

  useEffect(() => {
    if (!open || !form) return;

    if (mode === "create") {
      form.resetFields();
      form.setFieldsValue({
        emissionRates: [],
        minPpmByElement: [],
        solarRadiation: defaultSolarRadiationDubai,
      });

      updateMarkerPosition(defaultPosition, form);
      wizard.setActiveTab("1");
      return;
    }

    if (mode === "edit" && selectedEmissionPoint) {
      form.setFieldsValue({
        name: selectedEmissionPoint.name,
        latitude: selectedEmissionPoint.latitude,
        longitude: selectedEmissionPoint.longitude,
        height: selectedEmissionPoint.height,
        anemometerDeviceId: selectedEmissionPoint.anemometerDeviceId,
        concentrationHeight: selectedEmissionPoint.concentrationHeight,
        windThreshold: selectedEmissionPoint.windThreshold,
      });

      updateMarkerPosition(
        [selectedEmissionPoint.latitude, selectedEmissionPoint.longitude],
        form
      );

      emissionRatesHook.load();
      minPpmHook.load();
      solarRadiationHook.load();

      wizard.setActiveTab("1");
    }
  }, [open, mode, selectedEmissionPoint]);

  useEffect(() => {
    if (mode !== "edit") return;
    form.setFieldsValue({ emissionRates: emissionRatesHook.rates });
  }, [emissionRatesHook.rates]);

  useEffect(() => {
    if (mode !== "edit") return;
    form.setFieldsValue({ minPpmByElement: minPpmHook.values });
  }, [minPpmHook.values]);

  useEffect(() => {
    if (mode !== "edit") return;
    form.setFieldsValue({ solarRadiation: solarRadiationHook.values });
  }, [solarRadiationHook.values]);

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleSave = async () => {
    try {
      await form.validateFields();

      const missingMin = wizard.validateRatesHaveMinPpm();
      if (missingMin.length > 0) {
        setTimeout(() => {
          notification.warning({
            message: "Missing Min PPM values",
            description:
              "Each gas with emission rate must have a Min PPM value greater than 0.",
            placement: "top",
          });
        }, 0);
        wizard.setActiveTab("3");
        return;
      }

      const values = form.getFieldsValue(true);

      await onSave(values, form);
      setTimeout(() => {
        notification.success({
          message: wizard.isCreate
            ? "Emission point created"
            : "Emission point updated",
          description: wizard.isCreate
            ? "The emission point has been created successfully."
            : "The emission point has been updated successfully.",
          placement: "top",
        });
      }, 0);

      onCancel(); // opcional: cerrar modal tras guardar

    } catch (err) {
      console.error("SAVE FAILED", err);

      setTimeout(() => {
        notification.error({
          message: "Save failed",
          description:
            err?.message || "An unexpected error occurred while saving.",
        });
      }, 0);
    }
  };

  const isTabDisabled = (key) =>
    wizard.isCreate && Number(key) > Number(wizard.activeTab);

  const tabItems = [
    {
      key: "1",
      label: wizard.isCreate ? "Step 1 - Basic Info" : "Basic Info",
      children: (
        <EmissionPointBasicInfoTab
          form={form}
          mode={mode}
          markerPosition={markerPosition}
          showMap={showMap}
          mapRefreshKey={mapRefreshKey}
          setSmallMap={setSmallMap}
          openExpandedMap={openExpandedMap}
          updateMarkerPosition={updateMarkerPosition}
          devicesWithAnemometer={devicesWithAnemometer}
        />
      ),
    },
    {
      key: "2",
      label: wizard.isCreate ? "Step 2 - Emission Rates" : "Emission Rates",
      disabled: isTabDisabled("2"),
      children: <EmissionRatesTab form={form} />,
    },
    {
      key: "3",
      label: wizard.isCreate ? "Step 3 - Min PPM" : "Min PPM",
      disabled: isTabDisabled("3"),
      children: <MinPpmByElementTab form={form} />,
    },
    {
      key: "4",
      label: wizard.isCreate ? "Step 4 - Solar Radiation" : "Solar Radiation",
      disabled: isTabDisabled("4"),
      children: <SolarRadiationTab form={form} />,
    },
  ];


  return (
    <>
      <Modal
        title={wizard.isCreate ? "Create Emission Point" : "Edit Emission Point"}
        open={open}
        onCancel={handleCancel}
        footer={null}
        width={850}
        maskClosable={false}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ display: "flex", flexDirection: "column" }}
          onValuesChange={(changed, all) => {
            if ("latitude" in changed || "longitude" in changed) {
              const lat = Number(all.latitude);
              const lng = Number(all.longitude);
              if (!isNaN(lat) && !isNaN(lng)) {
                updateMarkerPosition([lat, lng], form);
              }
            }
          }}
        >
          <Tabs
            activeKey={wizard.activeTab}
            onChange={wizard.handleTabChange}
            destroyOnHidden={false}
            items={tabItems}
          />

          {/* Footer */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "12px 24px",
              borderTop: "1px solid #f0f0f0",
              marginTop: "auto",
            }}
          >
            <Space>
              <Button onClick={handleCancel}>Cancel</Button>

              {wizard.isCreate && wizard.isFirst && (
                <Button type="primary" onClick={wizard.next}>
                  Next
                </Button>
              )}

              {wizard.isCreate && !wizard.isFirst && !wizard.isLast && (
                <>
                  <Button onClick={wizard.back}>Back</Button>
                  <Button type="primary" onClick={wizard.next}>
                    Next
                  </Button>
                </>
              )}

              {wizard.isCreate && wizard.isLast && (
                <>
                  <Button onClick={wizard.back}>Back</Button>
                  <Button
                    type="primary"
                    loading={saving}
                    onClick={handleSave}
                  >
                    Create
                  </Button>
                </>
              )}

              {!wizard.isCreate && (
                <Button
                  type="primary"
                  loading={saving}
                  onClick={handleSave}
                >
                  Save
                </Button>
              )}
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Expanded Map */}
      <Modal
        open={expanded}
        onCancel={closeExpandedMap}
        footer={null}
        width={900}
        centered
      >
        <div style={{ height: 500, position: "relative" }}>
          <Button
            type="primary"
            style={{ position: "absolute", top: 18, right: 28, zIndex: 1200 }}
            onClick={closeExpandedMap}
          >
            Select
          </Button>

          <LocationPicker
            value={markerPosition}
            onChange={(pos) => updateMarkerPosition(pos, form)}
            setContainerMap={setExpandedMap}
            forceRefresh={expandedMapKey}
          />
        </div>
      </Modal>
    </>
  );
}
